import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useNotificaciones,
  type Notificacion,
  type NotificacionCategoria,
  getNotificacionIcon,
  getNotificacionColor,
  getNotificacionCategoria,
} from '@/hooks/useNotificaciones';
import { formatDistanceToNow, isToday, isYesterday, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Route mapping ─── */
function getNotificationRoute(n: Notificacion): string | null {
  switch (n.tipo) {
    case 'admin_nuevo_usuario': return '/admin/usuarios';
    case 'admin_documento_pendiente':
    case 'admin_verificacion_pendiente': return '/admin/verificacion';
    case 'admin_urgencia':
    case 'admin_nueva_solicitud': return '/admin/solicitudes';
    case 'admin_pago_recibido': return '/admin/pagos';
    case 'nueva_solicitud':
    case 'solicitud_urgente': return '/solicitudes';
    case 'solicitud_aceptada':
    case 'solicitud_enviada':
    case 'sin_profesionales':
    case 'solicitud_liberada': return '/mis-citas';
    case 'cita_confirmada':
    case 'cita_cancelada':
    case 'atencion_completada': return '/mis-citas';
    case 'verificacion_aprobada': return '/profesional';
    case 'documento_rechazado': return '/perfil';
    case 'nuevo_mensaje': return '/mis-citas';
    case 'nuevo_mensaje_colaboracion': return '/red-profesionales';
    default: return null;
  }
}

/* ─── Filter tabs ─── */
const FILTER_TABS: { label: string; value: NotificacionCategoria }[] = [
  { label: 'Todas', value: 'todas' },
  { label: '🚨 Urgentes', value: 'urgentes' },
  { label: '📅 Citas', value: 'citas' },
  { label: '💬 Mensajes', value: 'mensajes' },
  { label: '⚙️ Sistema', value: 'sistema' },
];

/* ─── Date grouping ─── */
function getDateGroup(dateStr: string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return 'Hoy';
  if (isYesterday(d)) return 'Ayer';
  return format(d, "d 'de' MMMM", { locale: es });
}

function groupByDate(items: Notificacion[]): { label: string; items: Notificacion[] }[] {
  const groups: Record<string, Notificacion[]> = {};
  const order: string[] = [];
  for (const n of items) {
    const label = getDateGroup(n.created_at);
    if (!groups[label]) {
      groups[label] = [];
      order.push(label);
    }
    groups[label].push(n);
  }
  return order.map(label => ({ label, items: groups[label] }));
}

/* ─── Main component ─── */
export function NotificacionesPopover() {
  const {
    notificaciones,
    hasUnread,
    unreadCount,
    filtro,
    setFiltro,
    marcarLeida,
    marcarTodasLeidas,
    eliminarNotificacion,
    eliminarTodas,
  } = useNotificaciones();
  const navigate = useNavigate();

  const handleClick = (n: Notificacion) => {
    if (!n.leida) marcarLeida(n.id);
    const route = getNotificationRoute(n);
    if (route) navigate(route);
  };

  const grouped = groupByDate(notificaciones);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', !hasUnread && 'text-muted-foreground/40')}
        >
          <Bell className="w-5 h-5" />
          {hasUnread && (
            <>
              <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-destructive rounded-full animate-pulse" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[calc(100vw-2rem)] sm:w-96 p-0 shadow-xl border-border max-w-96">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">Notificaciones</h3>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5">
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={marcarTodasLeidas}
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Leídas
              </Button>
            )}
            {notificaciones.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                onClick={eliminarTodas}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Borrar
              </Button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-border overflow-x-auto no-scrollbar">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setFiltro(tab.value)}
              className={cn(
                'text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap transition-all font-medium',
                filtro === tab.value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[60vh] sm:max-h-96">
          {notificaciones.length === 0 ? (
            <div className="py-10 text-center">
              <div className="text-3xl mb-2">🔔</div>
              <p className="text-sm text-muted-foreground">
                {filtro === 'todas' ? 'No tienes notificaciones' : 'Sin notificaciones en esta categoría'}
              </p>
            </div>
          ) : (
            <div className="py-1">
              {grouped.map(group => (
                <div key={group.label}>
                  <div className="px-4 py-1.5 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {group.label}
                    </p>
                  </div>
                  <AnimatePresence mode="popLayout">
                    {group.items.map((n) => (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <NotificacionItem
                          n={n}
                          onClick={() => handleClick(n)}
                          onMarkRead={() => marcarLeida(n.id)}
                          onDelete={() => eliminarNotificacion(n.id)}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Single notification item ─── */
function NotificacionItem({
  n,
  onClick,
  onMarkRead,
  onDelete,
}: {
  n: Notificacion;
  onClick: () => void;
  onMarkRead: () => void;
  onDelete: () => void;
}) {
  const icon = getNotificacionIcon(n.tipo);
  const colorClass = getNotificacionColor(n.tipo);
  const isUrgent = getNotificacionCategoria(n.tipo) === 'urgentes';

  return (
    <div
      className={cn(
        'mx-1.5 sm:mx-2 mb-1 rounded-lg px-2.5 sm:px-3 py-2 sm:py-2.5 transition-all cursor-pointer border-l-[3px] hover:shadow-sm',
        colorClass,
        !n.leida && 'ring-1 ring-primary/10',
        n.leida && 'opacity-70 hover:opacity-100'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        {/* Icon */}
        <span className={cn(
          'text-base sm:text-lg flex-shrink-0 mt-0.5',
          isUrgent && 'animate-bounce'
        )} style={isUrgent ? { animationDuration: '2s' } : {}}>
          {icon}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={cn(
              'text-xs sm:text-sm leading-tight',
              !n.leida ? 'font-semibold text-foreground' : 'text-muted-foreground'
            )}>
              {n.titulo}
            </p>
            {!n.leida && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
            )}
          </div>
          <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">
            {n.mensaje}
          </p>
          <p className="text-[10px] text-muted-foreground/50 mt-1">
            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: es })}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          {!n.leida && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground rounded-full"
              onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
              title="Marcar como leída"
            >
              <Check className="w-3 h-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground/50 hover:text-destructive rounded-full"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Eliminar"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
